import React from 'react'
import { Button } from '@/components/ui/button'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface Props {
  children: React.ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
  retryCount: number
}

export class ErrorBoundary extends React.Component<Props, State> {
  private maxRetries = 3
  private retryTimeout: NodeJS.Timeout | null = null

  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      retryCount: 0
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      retryCount: 0
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)
    this.setState({ errorInfo })
  }

  private handleRetry = () => {
    if (this.state.retryCount >= this.maxRetries) {
      window.location.reload()
      return
    }

    this.setState((prevState) => ({
      hasError: false,
      retryCount: prevState.retryCount + 1
    }))

    // Attempt automatic retry after a delay
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout)
    }

    this.retryTimeout = setTimeout(() => {
      if (this.state.hasError) {
        this.handleRetry()
      }
    }, 5000) // 5 second delay between retries
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout)
    }
  }

  render() {
    if (this.state.hasError) {
      const isNetworkError =
        this.state.error?.message.toLowerCase().includes('network') ||
        this.state.error?.message.toLowerCase().includes('fetch')

      const errorMessage = isNetworkError
        ? 'Network connection issue. Please check your internet connection.'
        : this.state.error?.message || 'An unexpected error occurred'

      return (
        <div className="min-h-[200px] p-6 flex flex-col items-center justify-center">
          <Alert variant="destructive" className="mb-4 max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="font-semibold">Something went wrong</div>
              <div className="text-sm mt-1">{errorMessage}</div>
              {this.state.retryCount > 0 && (
                <div className="text-xs mt-1">
                  Retry attempt {this.state.retryCount} of {this.maxRetries}
                </div>
              )}
            </AlertDescription>
          </Alert>

          <div className="flex gap-2">
            <Button
              onClick={this.handleRetry}
              variant="outline"
              className="flex items-center gap-2"
              disabled={this.state.retryCount >= this.maxRetries}>
              <RefreshCw className="h-4 w-4" />
              {this.state.retryCount >= this.maxRetries
                ? 'Reload Page'
                : 'Try Again'}
            </Button>
            {this.state.retryCount < this.maxRetries && (
              <Button onClick={() => window.location.reload()} variant="ghost">
                Reload Page
              </Button>
            )}
          </div>

          {import.meta.env.NODE_ENV === 'development' &&
            this.state.errorInfo && (
              <pre className="mt-4 p-4 bg-muted text-xs overflow-auto max-w-md max-h-[200px] rounded-md">
                {this.state.errorInfo.componentStack}
              </pre>
            )}
        </div>
      )
    }

    return this.props.children
  }
}
